import { useState } from 'preact/hooks';

import {
	Screenshot,
	Branding,
	ShiftFocusToTop,
	Group,
	CaptureWrapper
} from '@/features/elements';
import { getPageUrl, isLandingPage } from '@/utils';

import {
	useAdvanced,
	useConfigStore,
	useProperties,
	useFormProps
} from '../stores/ConfigStore';
import { useFormStore } from '../stores/FormStore';
import useBlock from '../hooks/useBlock';
import useIsTouchDevice from '../hooks/useIsTouchDevice';
import FormHeader from './FormHeader';
import Progress from './Progress';
import FormContent from './FormContent';

export default function BaseForm() {
	const { formKey, forceHeader, webView, webviewShowHeader } = useFormProps();
	const {
		contentDivRef,
		formType,
		formOpen,
		formIsCompleted,
		formIsOnOpenerPage,
		activePage,
		screenReaderHelperText
	} = useFormStore();
	const [formHeaderHeight, setFormHeaderHeight] = useState(0);

	const { themeClass } = useConfigStore();

	const { disable_branding, conversational } = useProperties();
	const advanced = useAdvanced();
	const isTouchDevice = useIsTouchDevice();

	const screenshotBlock = useBlock({
		typeName: 'screenshot'
	});

	const showFormHeader =
		formType !== 'embed' ||
		isLandingPage() ||
		forceHeader ||
		(webView && webviewShowHeader);
	const showProgress =
		!formIsCompleted && !formIsOnOpenerPage && advanced.showProgressbar;
	const showBranding =
		!disable_branding && (formType !== 'embed' || isLandingPage() || webView);

	return (
		<div
			class={`mopinion-survey-content notranslate wcag ${
				advanced.showProgressbar && !advanced.toggleDotview ? 'has-progressbar' : ''
			} ${advanced.buttonStyle ? advanced.buttonStyle : ''} ${
				!webView && formType === 'embed' ? 'overflow-visible' : ''
			} ${isTouchDevice ? 'touch-device' : ''}
			`}
			ref={contentDivRef}
			key="contentDiv"
			tabIndex={-1}
			data-nosnippet
		>
			<div
				id="screenreader-helper"
				role="status"
				aria-live="polite"
			>
				{screenReaderHelperText}
			</div>
			<div
				className={`mopinion-survey-output ${themeClass} ${
					formType === 'slide' ? 'mopinion-slide' : ''
				} ${formType === 'modal' ? 'is-modal' : ''} ${
					formType === 'embed' && !webView ? 'is-embed' : ''
				} on-${
					formIsCompleted
						? 'page-thanks'
						: formIsOnOpenerPage
						? 'page-intro'
						: 'page-' + activePage
				} ${conversational ? 'conversational' : ''}`}
				data-key={formKey}
				style={{
					position: 'relative'
				}}
				{...(formType !== 'embed' && {
					'data-mop-page-url': getPageUrl()
				})}
			>
				{showFormHeader && (
					<FormHeader
						formKey={formKey}
						formOpen={formOpen}
						formIsCompleted={formIsCompleted}
						setFormHeaderHeight={setFormHeaderHeight}
					/>
				)}
				{showProgress && <Progress />}
				<FormContent formHeaderHeight={formHeaderHeight} />
				{screenshotBlock?.block?.properties?.bottomScreen &&
					!formIsOnOpenerPage &&
					!formIsCompleted && (
						<Group
							block={screenshotBlock.block}
							show
						>
							<Screenshot
								block={screenshotBlock.block}
								state={screenshotBlock.state}
								setValue={screenshotBlock.actions.setValue}
								setNestedValue={screenshotBlock.actions.setNestedValue}
							/>
						</Group>
					)}
				{screenshotBlock.block?.id && !webView && (
					<CaptureWrapper id={screenshotBlock.block.id} />
				)}
			</div>
			{showBranding && <Branding />}
			{formType !== 'embed' && <ShiftFocusToTop formIsCompleted={formIsCompleted} />}
		</div>
	);
}
